// @ts-strict-ignore
import {
  AfterViewChecked,
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable, Observer, of, Subscription, SubscriptionLike as ISubscription } from 'rxjs';
import { SohoMessageService, SohoModalDialogService, SohoTabsComponent } from 'ids-enterprise-ng';
import { PageResource } from '@resources/page.resource';
import {
  LMComponentInfoService,
  LMDataService,
  LMDrillService,
  LMErrorUtilService,
  LMFormatUtilService,
  LMLoggerService,
  LMUrlInfoBuilderService,
  LMUrlUtilService
} from '@services/index';
import { LMPagePanelComponent } from './lm-page-panel.component';
import { LMPageToolbarService } from './lm-page-toolbar.service';
import { LMPagePersonalization } from '@states/models/lm-page-personalization.model';
import { LMPageStateKeyParams, LMPageStateService } from '@states/services/lm-page-state.service';
import {
  LMPagePersonalizationKeyParams,
  LMPagePersonalizationService
} from '@states/services/lm-page-personalization.service';
import { LMSpecService } from '@services/lm-spec.service';
import { SaveRequired } from '@base/lm-base';
import { LMBaseComponent, LMDialogInfo, LMRefreshRequest } from '@base/lm-base.component';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMRouteComponentService } from '@services/lm-route-component.service';
import { LMListSpecUtilService } from '@list/services/lm-list-spec-util.service';
import { LMHtmlPageUtilService } from '@html/lm-html-page-util.service';
import { LMFormManualRepService } from '@form/lm-form-manual-rep.service';
import { LMGlobalPersonalizationService } from '@states/services/lm-global-personalization.service';
import { LMContextService } from '@services/lm-context.service';
import { LMContextUtilService } from '@utils/lm-context-util.service';
import { AppConstants, LMDialogButtonKey } from '@utils/app.constants';
import { environment } from '@environments/environment';
import { LMToolbarComponent } from '@toolbar/lm-toolbar.component';
import { LMStateHelperService } from '@services/lm-state-helper.service';
import { LMConditionUtilService, LMGlobalUtilService, LMUniqueIdService } from '@utils/index';
import {
  LMActionFormDialogComponent,
  LMActionFormDialogResult,
  LMActionFormDialogResultCode,
  LMActionFormDialogService,
  LMPrintDialogService,
  LMPrintPdfInfo
} from '@modal/index';
import { filter, first } from 'rxjs/operators';
import { LMListComponent, LMListTransactionEvent } from '@list/lm-list.component';
import { LMWorker } from '../../types/lm-worker';
import { LMHtmlUrlDocService } from '@services/lm-html-url-doc.service';
import { LMApplicationMenuTransactionService, LMFormComponent, LMNavigationService } from '@app/components';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { LMAppComponentService } from '@utils/lm-app-component.service';
import { LMColorId, LMThemeService } from '@banner/lm-theme.service';
import { LMBannerHeaderComponentRef } from '../banner/lm-banner-header-component-ref.service';
import { LMComponentTrackerService } from '@services/lm-component-tracker.service';
import { KeyedResource } from '@app/resources';
import { LMWebAppService } from '@services/lm-webapp.service';
import { LMFormTransactionService } from '@form/services/lm-form-transaction.service';
import { LMTransactionState } from '@app/transaction';

declare let urltemplate: any;

export class LMPageSaveWorker extends LMWorker {
  private readonly panels: LMPagePanelComponent[];

  constructor(panel: LMPagePanelComponent[]) {
    super();

    this.panels = panel;
  }

  protected has(i: number): boolean {
    return (i < this.panels.length);
  }

  protected get(i: number): LMPagePanelComponent {
    return this.panels[ i ];
  }

  protected run(i: number): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const panel = this.panels[ i ];
      if (panel) {
        panel.save().subscribe(continueOn => {
          observer.next(continueOn);
          observer.complete();
        });
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }
}

@Component({
  selector: 'lm-page',
  templateUrl: './lm-page.component.html',
  styleUrls: [ 'lm-page.component.scss' ],
  providers: [
    LMPageToolbarService,
    LMListSpecUtilService,
    LMHtmlPageUtilService,
    LMHtmlUrlDocService,
    LMFormManualRepService,
    LMComponentTrackerService,
    LMDrillService
  ]
})
export class LMPageComponent extends LMBaseComponent  implements AfterViewChecked, AfterViewInit, OnDestroy, OnInit, SaveRequired {

  // ----------------------------------------------------------------------
  // INPUTS
  // ----------------------------------------------------------------------
  @Input() resourceId: string;
  @Input() pageSpec: LMPageSpec;

  // ----------------------------------------------------------------------
  // OUTPUTS
  // ----------------------------------------------------------------------
  @Output() sendContextMessageEvent = new EventEmitter<any>();

  // ----------------------------------------------------------------------
  // CHILDREN VARIABLES
  // ----------------------------------------------------------------------
  @ViewChildren('LMPagePanelComponent') pagePanelComponentList: QueryList<LMPagePanelComponent>;
  @ViewChild('dialogPlaceholder', { read: ViewContainerRef, static: true }) placeholder: ViewContainerRef;
  @ViewChild(LMToolbarComponent) toolbar: LMToolbarComponent;
  @ViewChild(SohoTabsComponent) tabs: SohoTabsComponent;
  @ViewChild('cartForm') cartForm: LMFormComponent;
  @ViewChild('sohoTabContainer', { read: ViewContainerRef, static: false }) headerTabs: ViewContainerRef;

  // ------
  // HOST BINDINGS
  // ----
  @HostBinding('id') public pageId;
  @HostBinding('attr.data-automation-id') public pageAutomationId;

  @HostBinding('style.height') get getHeight(): string {
    if (this.onMobile) {
      return 'inherit';
    } else {
      return this.hideTitleBar ? '100%' : null;
    }
  }

  @HostBinding('style.overflow') get overflow(): string | null {
    return this.hideTitleBar && this.hasContextForm ? 'auto' : null;
  }

  @HostBinding('class.has-context-form') public hasContextForm = false;
  @HostBinding('class.implements-card-view') get implementsCardView(): boolean {
    return this.pageSpec && this.pageSpec.pageSpec.hasCartForm;
  }
  @HostBinding('class.has-full-page-background') public hasFullPageBackground = false;
  @HostBinding('class.shopping-cart') get shoppingCart(): boolean {
    return this.pageSpec && this.pageSpec.pageSpec.hasCartForm;
  }
  @HostBinding('class.lm-banner-in-route') get classBannerInRoute() {
    return this.bannerInRoute;
  }
  @HostBinding('class.has-breadcrumbs') get hasBreadCrumbsDisplayed(): boolean {
    return this.hasBreadcrumbs;
  }

  // ----------------------------------------------------------------------
  // MEMBER VARIABLES
  // ----------------------------------------------------------------------
  public contextFormSpec: LMFormSpec;
  public contextFormUrlInfo: LMFormUrlInfo;
  public contextFormDataView: LMDataView;
  public dataView: LMDataView;
  public hasPanels = true;
  public lmTabsOptions: LMTabsOptions = {};
  public isProduction = environment.production;
  public personalizeTabs = false;
  public tabPanelContainerId = 'page-tab-panel-container';
  public legacyWebUI = false;
  public url3_5 = undefined;
  public overrideListTitle: string;
  public hideTitleBar = false;
  public toolbarButtons: Observable<Array<LMToolbarButton>>;
  public pageTitle: string;
  public toolbarSectionTitle: string;
  public screenId: string;
  public automationIdAttributes: SohoAttributesArray = [];
  public pagePanelTabAttributes: SohoAttributesArray = [];

  public isMultiPanel = false;
  public isSinglePanel = false;
  public isPersonalizable = false;
  public useAlternateTabs = false;
  public noPageDetailsString = '';
  public cartFormUrlInfo: LMFormUrlInfo = null;
  public bannerInRoute = false;
  public hasBreadcrumbs = false;

  public fullPageBackgroundImage: SafeStyle;
  public foregroundLight = false;
  public foregroundDark = false;

  private pageStateInfo: PageStateInfo = { isModified: false };
  private panels: Array<LMPagePanelSpec>;
  private pageResource: PageResource = null;
  private initialBuildComplete = false;
  private pageDataReturned = false;
  private tabActivatedSubscription: Subscription;

  /**
   * Whether or not this PageComponent is the root router-outlet component.
   * If it is then use the headerToolbarRef to set the toolbar view data.
   * Otherwise use the toolbar in this lists template.
   */
  public isRouterOutletRootComponent = false;

  /*
   * observable subscriptions. needed to unsubscribe when this
   * component is destroyed.
   */
  private subscriptions: ISubscription[] = [];

  private pageState: LMPageState = {};
  private pagePersonalization: LMPagePersonalization = {};
  private pageStateKeyParams: LMPageStateKeyParams;
  private pagePersonalizationKeyParams: LMPagePersonalizationKeyParams;
  readonly instanceId: string;

  public onMobile = false;
  private pageUrlInfo: LMPageUrlInfo;

  public testVersion1: boolean;
  public pagePanelTabsetId: string;
  public pagePanelTabsetAutomationId: string;

  private initializeSohoTabs = false;
  public useThemeColor = false;
  public useBackgroundImage = false;
  public backgroundImageUrl: SafeStyle;
  public lightContextForm = false;
  public contextFormColorClass: string;

  private _pendingSinglePanelSelection = false;
  public hasToolbar = false;
  public hasTabs = false;
  public useHeaderColorOnTabs = true;

  private relatedActonSubscription: ISubscription;

  constructor(
    private   appRef: ApplicationRef,
    private   changeDetectorRef: ChangeDetectorRef,
    public    elementRef: ElementRef,
    private   location: Location,
    private   route: ActivatedRoute,
    private   router: Router,
    private   zone: NgZone,
    public    sohoModalDialogService: SohoModalDialogService,
    private   sohoMessageService: SohoMessageService,
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    private   lmBannerHeaderCompRef: LMBannerHeaderComponentRef,
    private   lmComponentTrackerService: LMComponentTrackerService,
    private   lmContextService: LMContextService,
    private   lmWebAppService: LMWebAppService,
    private   lmErrorUtilService: LMErrorUtilService,
    private   lmLoggerService: LMLoggerService,
    private   lmHtmlPageUtilService: LMHtmlPageUtilService,
    private   lmActionFormDialogService: LMActionFormDialogService,
    private   lmPagePersonalizationService: LMPagePersonalizationService,
    private   lmPageStateService: LMPageStateService,
    private   lmStateHelperService: LMStateHelperService,
    private   lmPageToolbarService: LMPageToolbarService,
    private   currentComponent: LMRouteComponentService,
    private   lmSpecService: LMSpecService,
    private   lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private   lmContextUtilService: LMContextUtilService,
    protected lmComponentInfoService: LMComponentInfoService,
    protected lmUniqueIdService: LMUniqueIdService,
    private   lmGlobalUtilService: LMGlobalUtilService,
    private   lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private   lmUrlUtilService: LMUrlUtilService,
    private   lmPrintDialogService: LMPrintDialogService,
    protected  lmAppComponentService: LMAppComponentService,
    private   lmDataService: LMDataService,
    private   lmFormatUtilService: LMFormatUtilService,
    private   lmConditionUtilService: LMConditionUtilService,
    private   _sanitizer: DomSanitizer,
    private   lmThemeService: LMThemeService,
    private   lmNavigationService: LMNavigationService,
    private   lmFormTransactionService: LMFormTransactionService,
    private   lmApplicationMenuTransactionService: LMApplicationMenuTransactionService,
  ) {
    super(sohoModalDialogService, lmBusyIndicatorService, lmComponentInfoService, lmUniqueIdService, lmAppComponentService);
    this.instanceId = this.lmLoggerService.instanceId;
    this.onMobile = this.lmContextService.isMobileDevice();

    //  This is added for now to get by an issue where the preceived loading of the component is delayed.
    //  The constructor is called but then a 4-10 second delay is seen before the ngOnInit is called.
    //  The issue was written up but the issue closed as no example was submitted and the problem was not
    //  seen by a large number of people. Current plan is to try to dup the issue in a small example and
    //  submit. A link to the issue Below
    //  https://github.com/angular/angular/issues/18816
    setTimeout(() => {
      this.zone.run(() => {
        // CHANGE DETECTION
        if (this.url3_5) {
          this.changeDetectorRef.detectChanges();
        }
      });
    });

    const subscription = router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        // this.currentComponent.setRouteComponent(<Component> this);
        // if (this.initialBuildComplete) {
        //   // ---------------------------------------------------------------------
        //   // Called when this ListComponent object is reused by angular.
        //   // Call resetListComponent and build here as ngOnInit and
        //   // ngAfterViewInit will not be called in this scenarios.
        //   // ---------------------------------------------------------------------
        //   this.resetPageComponent();
        //   this.build();
        // }
        //
        // // setTimeout(() => {
        // //   appRef.tick();
        // // });
      } else if (event instanceof NavigationStart) {
        // show busy indicator when leaving a route.
        this.lmBusyIndicatorService.showBusyIndicator();

        // ---------------------------------------------------------------------
        // if starting a new navigation (leaving this route) then save this
        // lists state and personalizations
        // ---------------------------------------------------------------------
        // NOTE: if an access denied error happens then page won't be built.
        // Make sure to only saveState and personalizations if the component
        // is actyally built.
        // ---------------------------------------------------------------------
        if (this.initialBuildComplete) {
          if (!this.legacyWebUI) {
            this.saveComponentState();
          }
        }
      }
    });
    this.subscriptions.push(subscription);
  }

  // ----------------------------------------------------------------------
  // Component life cycle hooks.
  // ----------------------------------------------------------------------

  ngOnInit() {
    if (this.lmContextService.isAnyTrackingEnabled()) {
      this.lmLoggerService.time({ id: '>> Total Page ' + this.instanceId });
    }

    this.lmLoggerService.log([ this, this.instanceId, 'ngOnInit()' ]);

    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;

    this.bannerInRoute = this.lmContextService.isBannerInRoute();
    this.hideTitleBar = this.bannerInRoute ? true : this.lmContextService.isHideTitlebar();

    if (!this.pageSpec && !this.lmContextService.inMingleWidget() && (!this.hideTitleBar || this.bannerInRoute)) {
      this.isRouterOutletRootComponent = true;
      this.currentComponent.setRouteComponent(this as Component);
    }
  }

  public getSpec() {
    return this.pageSpec;
  }

  public isRouteReuseActive(): boolean {
    return this.routeReuseActive;
  }

  public setRouteReuseActive(active: boolean): void {
    this.routeReuseActive = active;
    if (active) {
      this.runRefreshAction();
    }
  }

  // todo ppatton could go into a service
  private setAutomationIds(): void {
    this.pageId = this.pageSpec.pageSpec.name + '_page';
    this.pagePanelTabsetId = this.pageSpec.pageSpec.name + '_tabs';

    this.pageAutomationId = 'lm-' + this.pageSpec.pageSpec.name + '-page';
    this.pagePanelTabsetAutomationId = 'lm-' + this.pageSpec.pageSpec.name + '-tabs';

    if (!this.testVersion1) {
      this.pagePanelTabAttributes.push({ name: 'id', value: this.pagePanelTabsetId + AppConstants.ELEMENT_ID_SUFFIX_FORM_PANEL_TABSET });
    }
    this.pagePanelTabAttributes.push({ name: 'data-automation-id', value: this.pagePanelTabsetAutomationId });
  }

  ngAfterViewInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngAfterViewInit()' ]);
    this.lmBusyIndicatorService.showBusyIndicator();

    this.hasToolbar = this.lmBannerHeaderCompRef.instance ? this.lmBannerHeaderCompRef.instance.hasToolbar : false;

    // -------------------------------------------------------------------------------------
    // need to call this in a secondary timeout that takes place in 40ms. Starting the busy
    // indicator takes 20ms by default so any code should be executed after that otherwise
    // angular will block the busy indicator.
    // -------------------------------------------------------------------------------------


    /**
     *  Need to set and pass url from outside of setTimeout()
     *  For some reason this.router.url ocassionally will have the /home path in the url
     *  when it has been referenced from within this.build().
     *  It appears as if this.router.url was probably set to early. While /home was still present (before redirect)
     *
     *  This causes an error to occur intermittently from within keyed.resource.ts
     *  https://jira.lawson.com/browse/LMCLIENT-39632
     */
    const url = this.router.url;
    setTimeout(() => this.build(url), 40);
    if (this.isRouterOutletRootComponent && this.bannerInRoute) {
      setTimeout(() => {
        if (this.headerTabs) {
          this.headerTabs.element.nativeElement.className = 'tab-container header header-tabs';
        }
      }, 500);
    }

    if (this.lmContextService.isBannerInRoute() && !this.lmContextService.isApplicationMenuV3()) {
      this.connectAppMenuButtonTrigger();
    }
  }

  ngAfterViewChecked() {
    if (this.initializeSohoTabs) {
      if (this.hasPanels && this.sohoTabsComponent) {
        this.initializeSohoTabs = false;
        this.sohoTabsComponent.updated();
        if (this.pageState.selectedTabId) {
          const pagePanel: LMPagePanelSpec = this.panels.find((panel: LMPagePanelSpec) => (panel.id === this.pageState.selectedTabId));
          const id = pagePanel ? pagePanel.id : this.panels[0].id;
          this.sohoTabsComponent.select(id, true);
        }
      }
    }

    if (this._pendingSinglePanelSelection) {
      this._pendingSinglePanelSelection = false;
      setTimeout(() => this.handleNewSinglePanelSelection());
    }
  }

  ngOnDestroy() {
    this.lmComponentTrackerService.endComponentTracking();
    this.setComponentDestroyed();
    this.tabActivatedSubscription?.unsubscribe();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];

    if (this.relatedActonSubscription) {
      this.relatedActonSubscription.unsubscribe();
    }

    this.lmPageToolbarService.reset();
  }

  public onAppMenuTriggerEvent(event) {
    this.lmAppComponentService?.getAppComponent()?.handleV3NavComponent();
  }

  // ----------------------------------------------------------------------
  // Router life cycle hooks.
  // ----------------------------------------------------------------------
  canDeactivate(): any {
    // verify that there are no outstanding changes, prompt the user if
    // the page lists, etc.. are dirty.
    return true;
  }

  public allowPageNavigation(): Promise<boolean> | boolean {
    if (this.checkSaveRequired()) {
      if (this.modalOpen()) {
        // let just ask if they want to stay,
        // difficult to determine the modal's purpose - it may run an action or it may just return unsaved data
        return this.displayNavigationConfirmation();
      } else {
        const youMayNavigateSource = new BehaviorSubject<boolean>(false);
        const youMayNavigate: Observable<boolean> = youMayNavigateSource.asObservable();

        const info: LMDialogInfo = { message: Soho.Locale.translate('UnsavedChangesContinuingMsg')  };
        this.handleUnsavedChanges(info).subscribe(continueOn => {
          youMayNavigateSource.next(continueOn);
          youMayNavigateSource.complete();
          this.hideBusyIndicator();
        });

        return youMayNavigate.pipe(filter(el => el)).toPromise();
      }
    }
    return true;
  }

  private handleUnsavedChanges(info: LMDialogInfo): Observable<boolean> {
    return new Observable<boolean>(observer => {
      if (this.isModified()) {
        this.showUnsavedChanges(info).subscribe((response: UnsavedChangesResponse) => {
          if (response === LMDialogButtonKey.YES) {
            this.runSaveAction(true, observer);
          } else if (response === LMDialogButtonKey.NO) {
            observer.next(true);
            observer.complete();
          } else {
            // Cancel.  Stay put and do nothing!
            observer.next(false);
            observer.complete();
          }
        });
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  checkSaveRequired() {
    return this.isModified() || this.modalOpen();
  }

  modalOpen() {
    return this.lmActionFormDialogService.hasOpenDialog;
  }

  displayNavigationConfirmation(): Promise<any> | boolean {
    const comp: LMActionFormDialogComponent = this.lmActionFormDialogService.getOpenDialog.componentDialog;
    const youMayNavigateSource = new BehaviorSubject<boolean>(false);
    const youMayNavigate: Observable<boolean> = youMayNavigateSource.asObservable();
    const dialogId = 'UnsavedChanges';
    if (comp?.formComponent?.isModified()) {
      const buttons = [
        // tslint:disable-next-line - shadowed variable e
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.LEAVE ]),
          text: Soho.Locale.translate(LMDialogButtonKey.LEAVE),
          click: (e, modal) => {
            this.lmActionFormDialogService.getOpenDialog.close(<LMActionFormDialogResult>{ result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL });
            youMayNavigateSource.next(true);
            youMayNavigateSource.complete();
            modal.close();
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.STAY ]),
          text: Soho.Locale.translate(LMDialogButtonKey.STAY),
          click: (e, modal) => {
            youMayNavigateSource.next(false);
            youMayNavigateSource.complete();
            modal.close();
            this.hideBusyIndicator();
          }
        }
      ];

      // Show the confirmation message dialog.
      // todo I18n
      this.sohoMessageService
        .message()
        .message(Soho.Locale.translate('NavigateAwayMessage'))
        .title(Soho.Locale.translate('UnsavedChangesMessage'))
        .buttons(buttons)
        .open();

    } else {
      // the form in the action form dialog component isn't dirty, so just return a boolean rather than
      // a promise attached to a confirmation
      this.lmActionFormDialogService.getOpenDialog.close();
      return true;
    }

    return (youMayNavigate.pipe(filter(el => el))).toPromise();
  }

  // ----------------------------------------------------------------------
  // PUBLIC METHODS
  // ----------------------------------------------------------------------

  // ----------------------------------------------------------------------
  // Event Handlers
  // ----------------------------------------------------------------------
  public onTitleChanged(event: any) {
    const titleChanged = event.detail;
    if (this.hideTitleBar) {
      this.pageTitle = titleChanged.newTitle;
    } else {
      this.toolbarSectionTitle = titleChanged.newTitle;
      this.lmBannerHeaderCompRef.instance.toolbarSectionTitle = this.toolbarSectionTitle;
      this.lmBannerHeaderCompRef.instance.toolbarMoreSettings = { beforeOpen: null, attachToBody: true };
    }
  }

  onToolbarButtonButtonEvent(toolbarButtonEvent: LMToolbarButtonEvent): void {
    this.lmLoggerService.trace([ this, this.instanceId, 'onToolbarButtonEvent()', toolbarButtonEvent ]);

    if (!toolbarButtonEvent.actionReference) {
      return;
    }
    if (toolbarButtonEvent.actionReference.id === AppConstants.STANDARD_TOOLBAR_REFRESH) {
      const jsonLog = {
        message: 'LMCLIENT UI ALERT: PAGE REFRESH CLICKED',
        webApp: this.lmContextService.getWebApp(),
        dataArea: this.lmContextService.getUrlPdlOrDa(),
        resourceId: this.resourceId
      };

      this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));

      const info: LMDialogInfo = { message: Soho.Locale.translate('UnsavedChangesContinuingMsg')  };
      this.handleUnsavedChanges(info).subscribe(response => {
        if (response) {
          this.runRefreshAction();
        }
      });
    } else if (toolbarButtonEvent.actionReference.id === AppConstants.STANDARD_SAVE) {
      this.runSaveAction(false);
    } else if (toolbarButtonEvent.actionReference.id === AppConstants.STANDARD_TOOLBAR_EXPORTPDF) {
      this.runPdfAction(toolbarButtonEvent.actionReference);
    } if (
        toolbarButtonEvent.actionReference &&
        toolbarButtonEvent.actionReference.type === AppConstants.BUTTON_TYPE_RELATED
    ) {
      this.runRelatedAction(toolbarButtonEvent.actionReference);
      return;
    } else if (toolbarButtonEvent.actionReference.type === AppConstants.BUTTON_TYPE_BUSTASK) {
      this.runBusinessTaskAction(toolbarButtonEvent.actionReference);
    }
  }

  onTabActivated(tabActivatedEvent: SohoTabsEvent): void {
    /*
     * onTabActivated gets messaged before the sohoDataGrid has finished resolving it's template
     * so I need to put into a setTimeout allowing angular to have a chance to process the template
     * before I attempt to call methods on it.
     */
    setTimeout(() =>
      this.activateTab(), 1);
  }

  onSendContextMessageEvent(event: any) {
    this.sendBusinessContext(event);
  }

  onUpdateToolbarStateEvent(event: any) {
    if (this.isMultiPanelPage) {
      this.pageStateInfo.isModified = this.isModified();
      this.pageStateInfo.selectedPanel = this.selectedPanel;
      this.updateToolbar();
    }
  }

  // ---------------------------------------------------
  // PRIVATE METHODS
  // ---------------------------------------------------

  private refreshPageData(): Observable<boolean> {
    if(!this.lmContextService.enablePageSpecRefactor()) {
      return of(true);
    }

    /*
      test the hashTagChanged feature                               - tested

      1.  single panel  to  single panel same panel                 - tested
      2.  single panel  to  single panel new panel                  - tested
      3.  single panel  to  multi panel same panel in selected      - tested
      4.  single panel  to  multi panel, new panel selected         - tested
      5.  multi panel   to  single panel same panel selected        - tested
      6.  multi panel   to  single panel new panel selected         - tested
      7.  multi panel   to  multi panel same panel selected
      8.  multi panel   to  multi panel new panel selected
      9.  single panel  to  no panels                               - tested
      10. multi panel   to  no panels
      11. no panels     to  single panel                            - tested
      12. no panels     to  multiple panel
     */
    return new Observable(observer => {
      this.lmDataService.getPageData(this.pageUrlInfo.initialDataUrl).subscribe((response: LMPageDataResponse) => {
        if (response.status === AppConstants.STATUS_COMPLETED) {
          if (response.serverLog) {
            this.lmLoggerService.serverLog(response.serverLog);
          }
          this.dataView = response.pageData.dataView;
          this.contextFormDataView = response.pageData.contextFormDataView;

          const wasSinglePanel = this.isSinglePanel;
          const oldSelectedPanelId = this.selectedPanel?.id;
          this.updatePanels();
          this.buildTabsOptions();
          this.updateTitle();
          this.updateOverrideTitle();

          this.hasPanels = this.panels && this.panels.length > 0;
          this.isMultiPanel = this.isMultiPanelPage;
          this.isSinglePanel = this.isSinglePanelPage;

          if (!this.hasPanels) {
            observer.next(true);
            observer.complete();
            return;
          }

          const newSelectedPanelId = this.lmTabsOptions.tabs[0].id;  // DOesnt work for t=multiple panels need to als handle case where selected tab is gone

          if (this.isSinglePanel && wasSinglePanel && newSelectedPanelId !== oldSelectedPanelId) {
            this.isSinglePanel = undefined; // need to force change detection on the template
            this._pendingSinglePanelSelection = true;
          } else if (this.isSinglePanel) {
            this._pendingSinglePanelSelection = true;
          } else if (this.isMultiPanel && wasSinglePanel) {
            /*
             * IMPORTANT: Put in timeout to ensure change detection happens before the bannerHeaderCompRef tabOptions are set.
             * This is required so that the tabset will update it's changes in the tab body area before the header tabs are
             * built. If that doesn't happen then the events from the soho tabset control will not fire correctly.
             *
             * This is also important for a single panel page (where we don't use the tab control) so that
             * the single lm-page-panel will be initialized and the pagePanelComponentList ViewChildren
             * is set properly.
           */
            // this.changeDetectorRef.detectChanges(); // DPB is this only need for banner tabs -  test that
            this.initializeSohoTabs = true;
          } else if (this.isMultiPanel) {
            // this.changeDetectorRef.detectChanges(); // DPB is this only need for banner tabs -  test that
            this.initializeSohoTabs = true;
          }

          observer.next(true);
          observer.complete();
        } else {
          const error: any = response;
          this.lmErrorUtilService.handleError(error.message, error.details, error.status, error.statusText);
          observer.next(false);
          observer.complete();
        }
      },
      (error: any) => {
        this.lmErrorUtilService.handleUnexpectedError(error, this.placeHolder);
        observer.next(false);
        observer.complete();
      });
    });
  }

  private runRefreshAction(originatingList?: string) {
    this.lmBusyIndicatorService.showBusyIndicator();

    let selectedTabIndex = 0;
    if (this.isMultiPanelPage) {

      // ----------------------------------------------------------------------
      // if Refreshing mark non selected tabs as needsReload
      // ----------------------------------------------------------------------
      selectedTabIndex = this.getActiveTabIndex();

      // ----------------------------------------------------------------------
      // If Refreshing mark non-selected tabs that have already been
      // activated as needsReload.
      // ----------------------------------------------------------------------
      this.pagePanelComponentList.toArray().forEach((panel, index) => {
        if (index !== selectedTabIndex && panel.activated) {
          //
          panel.needsReload = true;
        }
      });

      if (this.pageSpec.pageSpec.hasCartForm) {
        this.cartForm.runRefresh(
          Observable.create(observer => {
            observer.next(true);
            observer.complete();
          }));
      }
    }

    const pagePanelComponent: LMPagePanelComponent = this.pagePanelComponentList.toArray()[ selectedTabIndex ];
    const request = new LMRefreshRequest();
    request.originatingSourceId = originatingList;

    if (pagePanelComponent) {
      pagePanelComponent.refresh(request).subscribe(refreshed => {
        if (refreshed) {
          this.pageStateInfo.isModified = this.isModified();
          this.refreshPageData().subscribe();
          this.updateToolbar();
        }
        this.lmBusyIndicatorService.hideBusyIndicator();
      }, error => {
        this.lmBusyIndicatorService.hideBusyIndicator();
      });
    } else {
      this.refreshPageData().subscribe(refreshed => {
        this.lmBusyIndicatorService.hideBusyIndicator();
      });
    }
  }

  // hard refresh indicates that the user is doing an explicit refresh (ran the Refresh action)
  private runSaveAction(skipRefresh: boolean, callBack?: Observer<boolean>) {
    const pagePanelComponentArray = this.pagePanelComponentList.toArray();
    const modifiedPanels = pagePanelComponentArray.filter(pagePanelComponent => pagePanelComponent.isModified());

    const saveWorker: LMPageSaveWorker = new LMPageSaveWorker(modifiedPanels);
    return saveWorker.start().subscribe(continueOn => {
      if (continueOn && !skipRefresh) {
        this.runRefreshAction();
      }
      if (callBack) {
        callBack.next(continueOn);
        callBack.complete();
      }
    }),
    (err => {
      const errorComponent: LMPagePanelComponent = err;
      if (errorComponent) {
        // this.sohoTabsComponent.select('#' + errorPanel.panelId);
      }
      if (callBack) {
        callBack.next(false);
        callBack.complete();
      }
    });
  }

  private runPdfAction(actionReference: LMBusActionReference) {
    const pdfType = AppConstants.PAGE_PDF;
    const settings = <LMPrintPdfInfo>{
      resource: this.pageResource,
      fromResource: this.pageUrlInfo.sourceResource,
      actionReference,
      name: this.lmFormatUtilService.formatMessageRule(this.pageSpec.pageSpec.title, this.dataView),
      pdfType,
      selectedPanelName: this.selectedPanel?.panelName,
      fontOffset: this.pageSpec.pageSpec.fontOffset,
      orientation: this.pageSpec.pageSpec.orientation,
      scheduleActionAllowed: this.pageSpec.scheduleActionAllowed,
      includeRecordLimit: true,
      isDashboard: (this.selectedPanel?.panelType === 'DashBoardPanel')
    };

    const panelComponent = this.getActivePanelComponent();
    settings.listParams = {};
    panelComponent?.pagePaneListComponents?.forEach((comp) => {
      const list = comp.lmListComponent as LMListComponent;
      if (!list) {
        return;
      }

      // allow when atleast one pane is a list
      settings.includeSearchForm = true;
      const filterCriteria = list.getExportActionFilterCriteria().map((fc) => ({
        fieldId: fc.fieldId,
        operator: fc.operator,
        value: Array.isArray(fc.value) ? fc.value.join('|') : fc.value
      }));

      if (list?.listResource?.resourceId) {
        // parameters needed to properly build each list
        const params = {
          resourceId: list.listResource.resourceId,
          filterCriteria
        } as LMListParams;

        // set only chart name if current display is chart
        if (list.listState.displayAs === 'chart') {
          params.chartName = list.chartName;
        }

        settings.listParams[comp.pane.paneName || comp.panel.panelName] = params;
      } else {
        throw Error('runPdfAction list.listResource.resourceId is undefined');
      }
    });

    this.lmPrintDialogService.openPdfDialog(settings);
  }

  private runRelatedAction(
      actionReference: LMBusActionReference,
  ) {
    this.lmBusyIndicatorService.showBusyIndicator();
    const actionSpec: LMActionSpec = actionReference.actionSpec;

    const parsedUrlTemplate = urltemplate.parse(this.pageUrlInfo.relActionUrl);
    const relatedActionUrl = parsedUrlTemplate.expand({
      resourceId: actionReference.relBusClass,
      actionName: actionReference.name,
      formName:   actionReference.relBusForm
    });

    if (!actionSpec.hasParameters && actionSpec.type === AppConstants.ACTION_TYPE_CREATE) {
      // Exclude contextResourceId if coming from a related create action
      const actionRedirectUrl = this.lmUrlUtilService.buildActionRedirectUrl(this.pageUrlInfo, false, true);
      const createActionRedirectUrlTemplate = urltemplate.parse(actionRedirectUrl);
      const createActionRedirectUrl = createActionRedirectUrlTemplate.expand({
        resourceId:        actionReference.relBusClass,
        actionName:        actionSpec.name
      });

      this.lmNavigationService.runCreateActionNavigation(createActionRedirectUrl);
    } else {
      const resourceId: string = actionReference.relBusClass;
      this.relatedActonSubscription = this.lmFormTransactionService.runRelatedAction(
          relatedActionUrl,
          actionSpec,
          undefined, //LMActionInfo
          resourceId
      ).subscribe((actionResult: LMActionFormDialogResult) => {
        if (actionResult.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
          const result = actionResult.transactionStateResult.getTransactionResult();
          // Success

          if (!result.actionSpec?.suppressCompletionMessage) {
            const title = result?.actionSpec?.label;
            const message = result.message;
            this.lmGlobalUtilService.displayToastMessage(title, message, AppConstants.TRA_ACTION_COMPLETED);
          }

          if (result?.isCompletionNavigation && result?.redirectUrl) {
            this.lmNavigationService.runNavigation(result.redirectUrl);
          }
        } else if (actionResult.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OPENERROR) {
          // TODO: needs better error handling
          this.lmBusyIndicatorService.hideBusyIndicator();
          this.lmErrorUtilService.handleError(actionResult.openError.message, actionResult.openError.details, actionResult.openError.status);
        }
      });
    }
  }

  private runBusinessTaskAction(actionReference: LMBusActionReference) {
    const bustaskUrl = this.lmUrlUtilService.buildBusinessTaskUrl(this.pageUrlInfo, actionReference);
    if (!actionReference.relBusTaskDlg && !actionReference.willRunAsynchronous) {
        this.lmGlobalUtilService.navigateByUrl(bustaskUrl);
      return;
    } else {
      this.lmSpecService.getFormSpecFromDialogActionUrl(bustaskUrl)
        .subscribe((formResponse: LMFormSpecResponse) => {
          const formUrlInfo = formResponse.formUrlInfo;
          const formSpec = formResponse.formSpec;
          // const keyedResource = new KeyedResource({ resourceId: actionReference.relBusTask + '.' + actionReference.relBusTaskForm});

          const actionSpec: LMActionSpec = formSpec.busClassSpec.actions.find(a => a.name === actionReference.name);
          const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
          const url = urlCompiledTemplate.expand({ resourceId: actionReference.relBusTask, actionName: actionSpec.name });
          this.lmActionFormDialogService.getActionFormDialogDataView(url).pipe(first()).subscribe((responseDV: any) => {

            const formDataView: LMDataView = responseDV;
            if (formDataView.status !== AppConstants.STATUS_COMPLETED) {
              this.lmLoggerService.error(responseDV);
              this.lmErrorUtilService.handleError(responseDV.message, responseDV.details, responseDV.status, responseDV.statusText);
              return;
            }

            if (actionReference.willRunAsynchronous && !actionReference.relBusTaskDlg) {
              // Need to open as a schedule view
              this.lmApplicationMenuTransactionService.runMenuAction(formUrlInfo.transactionUrl, formSpec.busClassSpec, actionSpec, formDataView, this.placeholder).subscribe(
                  transactionState => {

                    this.lmBusyIndicatorService.hideBusyIndicator();

                    if (transactionState.isError) {
                      this.lmErrorUtilService.handleError(
                          transactionState.response.message,
                          transactionState.response.details,
                          transactionState.response.status,
                          transactionState.response.message);
                    } else if (transactionState.isCanceled) {
                      // What to do?
                    } else {
                      const result: LMActionResult = transactionState.getTransactionResult();

                      if (transactionState.isSuccess()) {
                        if (!result.actionSpec?.suppressCompletionMessage) {
                          const title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('ActionCompletedSuccessfully'), [ result.actionSpec.label ]);
                          const message = result.message || Soho.Locale.translate('ActionSuccessful');
                          this.lmGlobalUtilService.displayToastMessage(title, message, AppConstants.TRA_ACTION_COMPLETED);
                        }
                      } else {
                        this.lmErrorUtilService.handleError(
                            result.message,
                            result.details,
                            result.status,
                            result.message);
                      }

                      if (transactionState.actionFormDialogSaved && result.isCompletionNavigation && result.redirectUrl) {
                        this.lmNavigationService.runNavigation(result.redirectUrl);
                      }
                    }
                  });
            } else {
              this.lmActionFormDialogService.runActionFormDialog(formUrlInfo, formSpec, actionSpec, formDataView)
                .subscribe((actionFormDialogResult: LMActionFormDialogResult) => {
                  if (actionFormDialogResult.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
                    // The view exception and errors will be prompted before the dialog closes.
                    // So just print the success message here after the dialog closes
                    const transactionState: LMTransactionState = actionFormDialogResult.transactionStateResult;
                    const actionResult: LMActionResult = actionFormDialogResult.transactionStateResult.getTransactionResult();

                    if (!actionResult.actionSpec?.suppressCompletionMessage) {
                      this.lmGlobalUtilService.displayToastMessage(actionSpec.label, actionResult.message, AppConstants.TRA_ACTION_COMPLETED);
                    }

                    if (transactionState.isSuccess()) {
                      if (actionResult.isCompletionNavigation && actionResult.redirectUrl) {
                        this.lmNavigationService.runNavigation(actionResult.redirectUrl);
                      }
                    }
                  }
                });
            }
          });
        });
    }
  }

  private updateToolbar() {
    this.lmPageToolbarService.updateToolbar(this.pageSpec, this.pageStateInfo);
  }

  private isModified() {
    return this.pagePanelComponentList.toArray().some(pagePanelComponent => pagePanelComponent.isModified());
  }

  private resetPageComponent() {
    this.lmBusyIndicatorService.showBusyIndicator();

    this.url3_5 = undefined;
    this.legacyWebUI = false;
  }

  private activateTab() {
    if (!this.sohoTabsComponent) {
      return;
    }

    const tabIndex = this.getActiveTabIndex();

    if (tabIndex === -1) {
      return;
    }

    const pagePanelComponent = this.pagePanelComponentList.toArray()[ tabIndex ];
    this.pageState.selectedTabId = this.panels[tabIndex].id;
    this.pageStateInfo.selectedPanel = this.selectedPanel;

    if (pagePanelComponent) {
      this.activatePanel(pagePanelComponent);
    }

    // -----------------------------------------------------------------
    // When clicking a tab replace the url in browser history to
    // include the #tabName. This will ensure that if someone bookmarks
    // the page it will have an url hash that will bring them back to
    // that page panel.
    // -----------------------------------------------------------------
    const locationHref = this.location.path(true);
    const newPanelName = this.panels[tabIndex].panelName;
    const newUrl = this.lmUrlUtilService.updateUrlParams(locationHref, { selectedPanel : newPanelName });
    this.location.replaceState(this.lmUrlUtilService.toRelativeUrl(newUrl));
    this.sendShortcut(false);

    // const locationHref = this.location.path(true);
    // const urlObject = new URL(window.location.origin + locationHref);
    // const newPanelName = this.panels[tabIndex].panelName;
    //
    // if (newPanelName) {
    //   urlObject.hash = newPanelName;
    //   const newUrl = urlObject.href;
    //   this.location.replaceState(this.lmUrlUtilService.toRelativeUrl(newUrl));
    //   this.sendShortcut();
    // }

    // const locationHref = this.location.path(true);
    // const parts: string[] = locationHref.split('#');
    // const newPanelName = this.panels[tabIndex].panelName;
    // if (parts.length === 1 || (parts.length === 2 && parts[1] !== newPanelName)) {
    //   const newUrl = parts[0] + '#' + newPanelName;
    //   this.location.replaceState(this.lmUrlUtilService.toRelativeUrl(newUrl));
    //   this.sendShortcut();
    // }

    this.updateToolbar();
  }

  private activatePanel(pagePanelComponent: LMPagePanelComponent) {
    if (!pagePanelComponent.activated) {
      pagePanelComponent.activated = true;
      pagePanelComponent.needsReload = false; // safety
    } else if (pagePanelComponent.needsReload) {
      pagePanelComponent.refresh().subscribe(refreshed =>
        pagePanelComponent.needsReload = !refreshed);
    } else {
      pagePanelComponent.sendBusinessContext();
    }
  }

  onPanelContentDisplayed(arg: any) {}

  onPanelContentModified(fieldChangedEvent: LMListFieldChangedEvent) {
    this.pageStateInfo.isModified = this.isModified();
    this.lmPageToolbarService.updateToolbar(this.pageSpec, this.pageStateInfo);
  }

  onPanelDataUpdated(fieldChangedEvent: LMListFieldChangedEvent) {
    let originatingList: string;
    if (fieldChangedEvent instanceof LMListTransactionEvent) {
      const listTransactionEvent: LMListTransactionEvent = fieldChangedEvent;
      originatingList = listTransactionEvent.eventSource ? listTransactionEvent.eventSource.listId : null;
    }
    this.runRefreshAction(originatingList);
  }

  /**
   * Convenience method to return the sohoTabsComponent from the bannerHeaderRef.
   */
  private get sohoTabsComponent(): SohoTabsComponent {
    return  this.tabs;
  }

  private get selectedPanel(): LMPagePanelSpec {
    const tabIndex = this.getActiveTabIndex();
    return this.panels ? this.panels[tabIndex] : null;
  }

  private getActiveTabIndex(): number {
    if (this.hideTitleBar) {
      return this.tabs?.getActiveTab()?.closest('li').index();
    }

    const index = this.sohoTabsComponent?.getActiveTab()?.closest('li').index();
    return index || 0;
  }

  private getActivePanelComponent(): LMPagePanelComponent {
    const tabIndex = this.getActiveTabIndex();
    return this.pagePanelComponentList.toArray()[ tabIndex ];
  }

  public build(url: string): void {
    this.lmLoggerService.trace([ this, this.instanceId, 'build()' ]);

    if (this.isRouterOutletRootComponent && !this.bannerInRoute) {
      this.lmBannerHeaderCompRef.instance.fullPageScroll = this.hasContextForm;
    }

    this.initializeSohoTabs = true;
    /*
     * PageSpec is king. If a PageSpec is passed in as Input() then that
     * take precedence over the url.
     */
    if (this.pageSpec) {
      this.pageSpecLoaded();
    } else {
      this.loadPageSpec(url);
    }
  }

  private loadPageSpec(url: string): void {
    this.lmLoggerService.time({ id:  'LMPage.loadPageSpec() ' + this.instanceId });

    this.lmSpecService.getPageSpec(url).subscribe((response: LMPageSpecResponse | LMSystemException) => {
      this.lmLoggerService.timeEnd('LMPage.loadPageSpec() ' + this.instanceId);

      if (this.componentDestroyed) {
        return;
      }

      if ((response as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR) {
        this.hideBusyIndicator();
        const statusText = (response as LMSystemException).statusText;
        const details = (response as LMSystemException).details;
        this.lmErrorUtilService.handleError(statusText, details);
        return;
      }

      this.pageUrlInfo = (response as LMPageSpecResponse).pageUrlInfo;
      this.pageSpec = (response as LMPageSpecResponse).pageSpec;

      // The WebApp should be loaded, this is to ensure that MenuSecurity data
      // is on the client before panel and page menu item visibility is processed
      this.lmWebAppService.getWebAppMenuSpec(this.lmContextService.getWebApp()).subscribe(value => {
        this.pageSpecLoaded();
      });

      //  This is added for now to get by an issue where the perceived loading of the component is delayed.
      //  The constructor is called but then a 4-10 second delay is seen before the ngOnInit is called.
      //  The issue was written up but the issue closed as no example was submitted and the problem was not
      //  seen by a large number of people. Current plan is to try to dup the issue in a small example and
      //  submit. A link to the issue Below
      //  https://github.com/angular/angular/issues/18816
      this.zone.run(() => {
        // CHANGE DETECTION
        if (this.url3_5) {
          this.changeDetectorRef.detectChanges();
        }
      });
    },
    (error: any) => {
      this.hideBusyIndicator();
      this.lmErrorUtilService.handleUnexpectedError(error, this.placeholder);
    });
  }

  private updateTitle() {
    const titleMessageRule: any = this.pageSpec.pageSpec.title;
    const title = this.lmFormatUtilService.formatMessageRule(titleMessageRule, this.dataView);
    if (this.hideTitleBar) {
      this.pageTitle = title;
    } else {
      this.toolbarSectionTitle = title;
      this.lmBannerHeaderCompRef.instance.toolbarSectionTitle = this.toolbarSectionTitle;
      if (this.isRouterOutletRootComponent && !this.bannerInRoute && this.lmContextService.showBreadcrumbs()) {
        this.addToBreadcrumbs();
      }
    }
  }

  private addToBreadcrumbs() {
    if (!this.pageState.breadcrumbsByCategory) {
      this.pageState.breadcrumbsByCategory = {};
    }
    const category = this.getBreadcrumbCategory();

    let breadcrumbId = this.pageState.breadcrumbsByCategory[category] ? this.pageState.breadcrumbsByCategory[category] : undefined;
    breadcrumbId = this.lmBannerHeaderCompRef.updateBreadcrumbs({
      id: breadcrumbId,
      category: category,
      busClass: '',
      specName: this.pageSpec.pageSpec.name,
      type: 'PAGE',
      title: this.toolbarSectionTitle,
      routeUrl: this.router.url
    });
    this.pageState.breadcrumbsByCategory[category] = breadcrumbId;
    this.saveComponentState();
    this.hasBreadcrumbs = this.lmContextService.isBreadCrumbsDisplayed();
  }

  private getBreadcrumbCategory(): string {
    // if the request for he current component initiated in a custom homepage
    if (this.pageUrlInfo?.urlParams && AppConstants.CUSTOMHP in this.pageUrlInfo?.urlParams && this.pageUrlInfo?.urlParams[AppConstants.CUSTOMHP] === 'true') {
      return this.lmBannerHeaderCompRef.getCurrentCategory();
    }
    let category = this.getBreadcrumbMenuResourceCategory();
    if (category?.trim().length > 0) {
      return category;
    }
    let addUrlParam = true;
    if (this.pageUrlInfo?.urlParams && this.pageUrlInfo.urlParams[ AppConstants.BREADCRUMB_URL_PARAM ]) {
      category = this.pageUrlInfo.urlParams[ AppConstants.BREADCRUMB_URL_PARAM ];
      addUrlParam = false;
    }
    if (!category) {
      category = this.lmBannerHeaderCompRef.getCurrentCategory();
    }
    if (addUrlParam) {
      const url = this.router.url + ((this.router.url.indexOf('?') > -1) ? '&' : '?') + AppConstants.BREADCRUMB_URL_PARAM + '=' + category;
      this.location.replaceState(this.lmUrlUtilService.toRelativeUrl(url, true, true));
    }
    return category;
  }
  private getBreadcrumbMenuResourceCategory(): string {
    let category: string;
    if(this.pageUrlInfo.sourceResource?.resourceType === 'menu') {
      category = this.lmContextService.getWebApp() + '.' + this.pageUrlInfo.sourceResource.resourceId;
    }
    return category;
  }
  private updateOverrideTitle() {
    if (this.hasPanels) {
      const titleMessageRule: any = this.panels[0].panelTitle;
      const title = this.lmFormatUtilService.formatMessageRule(titleMessageRule, this.dataView);
      this.overrideListTitle = this.isSinglePanelPage ? title : null;
    }
  }

  private buildPanelTitle(panel: LMPagePanelSpec): string {
    const titleMessageRule: any = panel.panelTitle;
    return this.lmFormatUtilService.formatMessageRule(titleMessageRule, this.dataView);
  }

  private updatePanels() {
    this.panels = [];

    for (let i = 0, len = this.pageSpec.pageSpec.panels.length; i < len; i++)  {
      const panel = this.pageSpec.pageSpec.panels[ i ];

      if (this.lmWebAppService.isPagePanelInaccessible(panel)) {
        continue;
      }

      if (panel.visibility) {
        if (!this.lmConditionUtilService.isValid([ panel.visibility ], this.dataView)) {
          continue;
        }
      }

      if (!this.panelHasContent(panel)) {
        continue;
      }

      if (panel.validity) {
        if (!this.lmConditionUtilService.isValid([ panel.validity ], this.dataView)) {
          continue;
        }
      }

      this.panels.push(panel);
    }

    this.hasPanels = this.panels && this.panels.length > 0;
    this.isMultiPanel = this.isMultiPanelPage;
    this.isSinglePanel = this.isSinglePanelPage;
    // this.singlePanel = this.isSinglePanel ? this.panels[0] : null;
  }

  private load(): void {
    this.lmDataService.getPageData(this.pageUrlInfo.initialDataUrl).subscribe((response: LMPageDataResponse) => {
      if (response.status === AppConstants.STATUS_COMPLETED) {

        if (this.componentDestroyed) {
          return;
        }

        if (response.serverLog) {
          this.lmLoggerService.serverLog(response.serverLog);
        }
        this.pageDataReturned = true;
        this.dataView = response.pageData.dataView;
        this.contextFormDataView = response.pageData.contextFormDataView;
        this.pageSpecLoaded();
      } else {
        const error: any = response;
        this.lmErrorUtilService.handleError(error.message, error.details, error.status, error.statusText);
      }
    },
    (error: any) => {
      this.lmErrorUtilService.handleUnexpectedError(error, this.placeHolder);
    });
  }

  private pageSpecLoaded(): void {

    // ------------------------------------------------------------------------
    // can set to true here since the build method will have provided all
    // that's needed to continue building the component.
    // ------------------------------------------------------------------------

    if (!this.pageSpec.pageSpec.messageRuleView && !this.pageDataReturned) {
      // data not brought back with spec -- new way not ready to build yet
      this.load();
      return;
    }

    this.initialBuildComplete = true;

    this.processPageSpec();
    this.setAutomationIds();

    // this.pageId = this.pageSpec.pageSpec.name + '_page';
    // this.pageAutomationId = this.pageSpec.pageSpec.name + '-page';
    //
    // this.pagePanelTabsetId = this.pageSpec.pageSpec.name + AppConstants.ELEMENT_ID_SUFFIX_PAGE_PANEL_TABSET;
    // this.pagePanelTabsetAutomationId = this.pageSpec.pageSpec.name + '-tabset';
    //
    // this.automationIdAttributes = [ {
    //   name: 'id', value: this.pagePanelTabsetId
    // }, {
    //   name: 'data-automation-id', value: this.pagePanelTabsetAutomationId
    // } ];
    this.pageId = this.pageSpec.pageSpec.name + '_page';
    this.pagePanelTabsetId = this.pageSpec.pageSpec.name; //DPB Need to resolve with PHIL + AppConstants.ELEMENT_ID_SUFFIX_PAGE_PANEL_TABSET;
    this.automationIdAttributes = [ { name: 'id', value: this.pagePanelTabsetId } ];

    const bannerPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    const appDisplay = bannerPersonalization.appDisplay;
    const unsupportedPattern = this.doesSpecContainUnsupported4_0Patterns(this.pageSpec);
    this.setLegacyVariables(unsupportedPattern, appDisplay, this.lmContextService, this.lmHtmlPageUtilService);
    if (this.lmBannerHeaderCompRef.instance && this.lmBannerHeaderCompRef.instance.is3_5 !== undefined) {
      this.lmBannerHeaderCompRef.instance.is3_5 = (this.url3_5 && this.url3_5.length > 0) ? true : false;
    }

    this.updatePanels();
    if (this.pageSpec.pageSpec.hasCartForm) {
      const cartFormSpec: LMFormSpec = this.pageSpec.pageSpec.cartForm;
      this.cartFormUrlInfo = this.lmUrlInfoBuilderService.getFormUrlInfoFromFormSpec(cartFormSpec);
    }

    this.loadComponentState();
    this.initialize();
    this.buildToolBar();
    this.buildTabsOptions();
    this.buildPageBackground();
    this.lmTabsOptions.containerElementSelector = '#' + this.tabPanelContainerId;
    this.sendShortcut();
    this.buildHTMLVariables();

    this.updateOverrideTitle();

    if (this.needSendBusinessMessage) {
      this.sendBusinessContext();
    }

    /*
     * IMPORTANT: Put in timeout to ensure change detection happens before the bannerHeaderCompRef tabOptions are set.
     * This is required so that the tabset will update it's changes in the tab body area before the header tabs are
     * built. If that doesn't happen then the events from the soho tabset control will not fire correctly.
     *
     * This is also important for a single panel page (where we don't use the tab control) so that
     * the single lm-page-panel will be initialized and the pagePanelComponentList ViewChildren
     * is set properly.
     */
    this.changeDetectorRef.detectChanges();

    if (!this.url3_5) {
      // Need to setup this listener always because the page have refresh and then have tabs
      // const tabActivatedSubscription = this.lmBannerHeaderCompRef.instance.tabActivated.subscribe(
      //   (tabActivatedEvent: SohoTabsEvent ) => this.onTabActivated(tabActivatedEvent));
      // this.subscriptions.push(tabActivatedSubscription);

      if (!this.isMultiPanelPage) {

        const pagePanelComponent = this.pagePanelComponentList.toArray()[0];
        if (pagePanelComponent) {
          this.activatePanel(pagePanelComponent);
        }
      }

      if (!this.hideTitleBar && !this.lmContextService.inMingleWidget()) {
        const toolbarSubscription = this.lmBannerHeaderCompRef.instance.toolbarButtonEvent.subscribe(
          (toolbarButtonEvent: LMToolbarButtonEvent) => this.onToolbarButtonButtonEvent(toolbarButtonEvent));
        this.subscriptions.push(toolbarSubscription);
      }
    }

    const loadTime = this.lmLoggerService.timeEnd('>> Total Page ' + this.instanceId, [ this, 'pageSpecLoaded()', 'end' ]);
    this.sendPageAnalytics(this.route?.routeConfig?.component?.name, loadTime);
  }

  private initialize(): void {
    if (this.pageUrlInfo && this.pageUrlInfo.httpUrl) {
      // ------------------------------------------------------------------------
      // set pageState.selectedTabId to the hashtag (the panel name).
      // This will cause the page to open to that tab.
      // ------------------------------------------------------------------------
      const targetPanelName = this.pageUrlInfo.urlParams['selectedPanel']; // this.lmUrlUtilService.getUrlParam(this.pageUrlInfo.href, 'selectedPanel');
      if (targetPanelName) {
        const pagePanel: LMPagePanelSpec = this.panels.find((panel: LMPagePanelSpec) => (panel.panelName === targetPanelName));

        if (pagePanel) {
          this.pageState.selectedTabId = pagePanel.id;
          this.pageStateInfo.selectedPanel = pagePanel;
        }
      }
    }

    // if (this.pageUrlInfo && this.pageUrlInfo.url && this.pageUrlInfo.url.hash) {
    //   // ------------------------------------------------------------------------
    //   // set pageState.selectedTabId to the hashtag (the panel name).
    //   // This will cause the page to open to that tab.
    //   // ------------------------------------------------------------------------
    //   const targetPanelName = this.pageUrlInfo.url.hash.substring(1);
    //   const pagePanel: LMPagePanelSpec = this.panels.find((panel: LMPagePanelSpec) => (panel.panelName === targetPanelName));
    //
    //   if (pagePanel) {
    //     this.pageState.selectedTabId = pagePanel.id;
    //     this.pageStateInfo.selectedPanel = pagePanel;
    //   }
    // }

    if (this.pageSpec.pageSpec.contextFormInfo) {
      this.buildContextFormInfo();
    }

    if (this.pageSpec.pageSpec.messageRuleView) {
      this.dataView = this.pageSpec.pageSpec.messageRuleView;
    }

    this.pageResource = new PageResource({ resourceId: this.pageSpec.resourceId });
    this.pageStateInfo.selectedPanel = this.selectedPanel;

    if (!this.hasPanels) {
      this.lmBusyIndicatorService.hideBusyIndicator();
    }
  }

  private processPageSpec(): void {
    if (!this.lmContextService.useNewSpecUrls()) {
      return;
    }

    /**
     * for new spec urls the relationResourceId has field names instead of
     * field values in it. These need to be resolved against the page dataView.
     */
    this.pageSpec.pageSpec.panels.forEach((panel: LMPagePanelSpec) => {
      if (panel.panelType === 'Panel') {
        const paneSpec: LMPagePaneSpec = panel.pane;
        if (paneSpec && paneSpec.relationResourceId) {
          paneSpec.relationResourceId = this.processRelationResourceId(paneSpec.relationResourceId, this.dataView);
        }
      } else if (panel.panelType === 'MultiPanePanel') {
        panel.panes.forEach((pane: LMMultiPagePaneSpec) => {
          const paneSpec: LMPagePaneSpec = pane.pane;
          if (paneSpec && paneSpec.relationResourceId) {
            paneSpec.relationResourceId = this.processRelationResourceId(paneSpec.relationResourceId, this.dataView);
          }
        });
      } else if (panel.panelType === 'DashBoardPanel' || panel.panelType === 'MultiListPanel') {
        panel.panes.forEach((pane: LMMultiPagePaneSpec) => {
          // todo another annoyance: In the case of dashboardPanels panel.panes appears
          //  to be a LMMultiPagePaneSpec[]. In that type there is no relationResourceId
          //  but it has been added to it. The correct type fix would be to add it to the
          //  LMMultiPagePaneSpec.pane like it is with a panel.panelType of 'MultiPanePanel'
          const paneSpec = pane as any;
          if (paneSpec && paneSpec.relationResourceId) {
            paneSpec.relationResourceId = this.processRelationResourceId(paneSpec.relationResourceId, this.dataView);
          }
        });
      }
    });
  }

  private processRelationResourceId(relationResourceId: string, dataView: LMDataView): string {
    const relationResource = new KeyedResource( { resourceId: relationResourceId, dataView: dataView });
    return relationResource.resourceId;
  }

  /**
   * Lazily builds and returns the LMListStateKeyParams object.
   * @returns
   */
  private buildPageStateKeyParams(): LMPageStateKeyParams {
    if (!this.pageStateKeyParams) {
      this.pageStateKeyParams = this.lmStateHelperService.buildPageStateKeyParams(this.route.snapshot.params['resourceId'], this.pageSpec, this.elementRef.nativeElement.id);
    }
    return this.pageStateKeyParams;
  }

  private buildPagePersonalizationKeyParams(): LMPagePersonalizationKeyParams {
    if (!this.pagePersonalizationKeyParams) {
      this.pagePersonalizationKeyParams = new LMPagePersonalizationKeyParams();
      this.pagePersonalizationKeyParams.prefix = this.elementRef.nativeElement.id;
      this.pagePersonalizationKeyParams.dataArea = this.lmContextService.getContext().urlPdlOrDa;
      this.pagePersonalizationKeyParams.resourceId = this.pageSpec.resourceId;
      this.pagePersonalizationKeyParams.actorId = this.lmContextService.getContext().actorId;
      if (this.lmContextService.getContext().asOfDate) {
        this.pageStateKeyParams.asOfDate = this.lmContextService.getContext().asOfDate;
      }
    }

    return this.pagePersonalizationKeyParams;
  }

  private buildTabsOptions() {
    this.lmTabsOptions.tabs = [];
    for (let i = 0, len = this.panels.length; i < len; i++) {
      const panel = this.panels[ i ];
      const tabItem: LMTab = {
        id: panel.id,
        automationId: panel.automationId,
        panel,
        title: this.buildPanelTitle(panel),
        loaded: false
      };

      if (this.pageState.selectedTabId && this.pageState.selectedTabId === this.panels[i].id) {
        tabItem.selected = true;
      }

      this.lmTabsOptions.tabs.push(tabItem);
    }
    this.lmTabsOptions.containerElementSelector = '#' + this.tabPanelContainerId;
  }

  private buildPageBackground() {
    if (this.pageSpec?.pageSpec?.background) {
      this.hasFullPageBackground = true;
      this.fullPageBackgroundImage = this._sanitizer.bypassSecurityTrustStyle(`url('${this.pageSpec.pageSpec.background.image}')`);

      if (this.pageSpec.pageSpec.background.foregroundText) {
        this.foregroundDark = this.pageSpec.pageSpec.background.foregroundText === 'dark';
        this.foregroundLight = this.pageSpec.pageSpec.background.foregroundText === 'light';
      }
    }
  }

  private buildHTMLVariables(): void {
    this.isPersonalizable = this.canPersonalize();
    this.useAlternateTabs = this.shouldUseAlternateTabs();
    this.noPageDetailsString = this.noPageDetailsMessage;
  }

  private buildToolBar() {
    if (this.lmContextService.inMingleWidget()) {
      return;
    }

    // toolbar already appends '_toolbar' to the so removing adding it here for v2.
    // need to keep it for v1 in order to not break current usage.
    this.screenId = this.pageSpec.pageSpec.name;

    this.lmLoggerService.log([ this, this.instanceId, 'buildToolbar()' ]);
    if (!this.url3_5) {
      if (this.hideTitleBar) {
        this.toolbarButtons = this.lmPageToolbarService.getToolbarButtonsObservable();
      } else {
        this.lmBannerHeaderCompRef.instance.screenId = this.screenId;
        this.lmBannerHeaderCompRef.instance.screenAutomationId = this.screenId;
        this.lmBannerHeaderCompRef.instance.toolbarButtons = this.lmPageToolbarService.getToolbarButtonsObservable();
        this.lmBannerHeaderCompRef.instance.toolbarMoreSettings = this.toolbarMoreMenuSettings;
      }
    }

    this.updateTitle();

    setTimeout(() => {
      // ------------------------------------------------------------------
      // need to place in a timeout so the toolbarService has a change to
      // set it's observable member.
      // ------------------------------------------------------------------
      this.lmPageToolbarService.buildToolbarButtons(this.pageSpec, this.pageStateInfo);
    });
  }

  private sendPageAnalytics(componentName: string, loadTime: number): void {
    this.lmComponentTrackerService.endLoadTracking(componentName, this.router.url, this.getComponentInfo(), loadTime);
    this.lmComponentTrackerService.startComponentTracking(this.getComponentInfo(), loadTime);
  }

  private sendShortcut(performClearAll = true): void {
    const shortcutTitle = `${this.toolbarSectionTitle} / ${this.lmContextService.getContext().webAppTitle}`;
    this.lmContextUtilService.enableStandardShortcut(performClearAll, shortcutTitle);
  }

  private sendBusinessContext(contextMsgs?: any): void {
    this.lmContextUtilService.clearAll();
    if (contextMsgs) {
      this.lmContextUtilService.sendContextMessages(contextMsgs);
    } else {
      this.lmContextUtilService.buildContextMessages({
        busFormName:           this.pageSpec.pageSpec.name,
        busClassName:          this.lmContextService.getContext().webApp, // since no busclass for a page use webApp name.
        widgetEventPrefix:     AppConstants.RESOURCETYPE_PAGE,
      })
        .subscribe((msgObj: any) => {
          if (msgObj.status === AppConstants.STATUS_COMPLETED) {
            this.lmContextUtilService.sendContextMessages(msgObj);
          }
        });
    }
  }

  get isMultiPanelPage(): boolean {
    if (this.panels) {
      return this.panels.length > 1;
    }
    return false;
  }

  get isSinglePanelPage(): boolean {
    if (this.panels) {
      return this.panels.length === 1;
    }
    return false;
  }

  get needSendBusinessMessage(): boolean {
    // if the first pane on the panel is not a list initiate a message
    if (this.panels && this.panels.length === 0 ) {
      return;
    }

    if (this.panels[0].pane) {
      return this.panels[0].pane.paneType === 'MenuPanel';
    } else if (this.panels[0].panelType === 'DashBoardPanel') {
      return true;
    }
    return false;
  }

  get noPageDetailsMessage(): string {
    return Soho.Locale.translate('NoPageDetailsExist');
  }

  public getUserWebRequestId(): string {
    return  this.isRouterOutletRootComponent ? (this.pageSpec ?  this.pageSpec.pageSpec.name : 'NO_PAGE_SPEC') + '-' +this.getBaseCompId()
      : this.getRouteCompUserWebRequestId();
  }

  public getComponentInfo(additionalInfo?: LMCompInfoAdditionalInfo): LMComponentInfo {
    // let compiledTemplate = urltemplate.parse(this.pageSpec);
    // let loadUrl = compiledTemplate.expand({ resourceId: this.model.getData().resourceId });

    const resourceInfo = this.pageSpec && this.pageSpec.pageSpec && this.pageSpec.pageSpec.resourceInfo ? this.pageSpec.pageSpec.resourceInfo : undefined;
    const componentConfigInfo: ConfigInfo = {};

    componentConfigInfo['personalizeTabs'] = this.personalizeTabs;
    componentConfigInfo['personalization'] =  this.pageSpec ? this.pageSpec.pageSpec.personalization : undefined;
    componentConfigInfo['configuration'] =  this.pageSpec ? this.pageSpec.pageSpec.configuration : undefined;
    componentConfigInfo['configStamp'] = this.pageSpec ? this.pageSpec.pageSpec.configStamp : 0;
    componentConfigInfo['classConfigStamp'] = this.pageSpec ? this.pageSpec.pageSpec.classConfigStamp : 0;
    componentConfigInfo['isReport'] = this.pageSpec?.pageSpec?.isReport;
    componentConfigInfo['folder'] = this.pageSpec?.pageSpec?.folder;

    const pageInfo: LMComponentInfo = {
      componentType: 'PAGE',
      userWebRequestId: this.getUserWebRequestId(),
      name: this.pageSpec ? this.pageSpec.pageSpec.name : this.getCompNameFromRoute(this.route.snapshot, undefined),
      type: 'page',
      developer: this.pageSpec ? this.pageSpec.developer : undefined,
      module: this.pageSpec ? this.pageSpec.module : undefined,
      resourceId: this.resourceId,
      pageSpec: JSON.stringify(this.pageSpec, null, 4),
      hasPanels: this.hasPanels,
      //    lmTabsOptions:  JSON.stringify(this.lmTabsOptions, null, 4),
      overrideListTitle: this.overrideListTitle,
      hideTitleBar:  this.hideTitleBar,
      pageTitle: this.pageTitle,
      configInfo: componentConfigInfo,
      lpl: this.pageSpec ? this.pageSpec.pageSpec.lpl : undefined,
      resourceInfo,
      tenant: this.lmContextService.getContext() ? this.lmContextService.getContext().dataArea : undefined,
      hasConfigAccess: this.lmContextService.hasConfigAccess(),
    };

    return pageInfo;
  }

  public getComponentPlaceHolder() {
    return this.placeholder;
  }

  loadComponentState(): void {
    if (this.lmContextService.disablePageState()) {
      return;
    }

    if (this.lmContextService.getClearPageState()) {
      if (this.lmContextService.resetBreadCrumbs() && this.isRouterOutletRootComponent && !this.bannerInRoute  && this.lmContextService.showBreadcrumbs()) {
        this.lmBannerHeaderCompRef.resetBreadCrumbs(this.getBreadcrumbMenuResourceCategory());
      }

      this.clearComponentState(true);
      this.lmContextService.setClearNextPageState(false);
    }

    this.pageState = this.lmPageStateService.getPageState(this.buildPageStateKeyParams());
    this.pagePersonalization = this.lmPagePersonalizationService.getPagePersonalization(this.buildPagePersonalizationKeyParams());
  }

  saveComponentState(includeSubComponents?: boolean) {
    this.lmPageStateService.savePageState(this.buildPageStateKeyParams(), this.pageState);
    if (includeSubComponents) {
      this.pagePanelComponentList.forEach((panel) => {
        panel.savePanelState();
      });
    }
    this.lmPagePersonalizationService.savePagePersonalization(this.buildPagePersonalizationKeyParams(), this.pagePersonalization);
  }

  clearComponentState(includeSubComponents?: boolean): void {
    this.lmStateHelperService.clearPageComponentState(this.route.snapshot.params['resourceId'], this.pageSpec, this.elementRef.nativeElement.id, includeSubComponents);
  }

  // hashTagChanged(url: string, newHashTag: string): boolean {
  //   if (this.isSinglePanelPage) {
  //     return true;  // if single panel page then your already on it.
  //   }
  //
  //   const targetPanelName = newHashTag;
  //
  //   // ------------------------------------------------------------------------
  //   // 1. activate panel associated with newHasTag passed in
  //   // ------------------------------------------------------------------------
  //   const pagePanel: LMPagePanelSpec = this.panels.find((panel: LMPagePanelSpec) => (panel.panelName === targetPanelName));
  //
  //   if (!pagePanel) {
  //     return false;
  //   }
  //
  //   // (this.tabs as any).activate(pagePanel.id);
  //   this.sohoTabsComponent.select(pagePanel.id);
  //
  //   this.lmBusyIndicatorService.hideBusyIndicator();
  //
  //   // ------------------------------------------------------------------------
  //   // 2. replace the url w/o the hashtag in the location service
  //   // ------------------------------------------------------------------------
  //   this.location.replaceState(this.lmUrlUtilService.toRelativeUrl(url));
  //   return true;
  // }

  shouldUseAlternateTabs() {
    // need to set the alternate class on the tab so there will be a background color assigned.
    return !this.useHeaderColorOnTabs;
  }

  canPersonalize() {
    return true;
  }

  private buildContextFormInfo(): void {
    this.hasContextForm = true;
    this.useHeaderColorOnTabs = false;
    if (!this.hideTitleBar) {
      this.lmBannerHeaderCompRef.instance.fullPageScroll = true;
    }

    const contextFormBusSpec = this.pageSpec.pageSpec.contextFormInfo.busFormSpec;
    this.buildBackgroundInfo(contextFormBusSpec);

    this.contextFormSpec = {
      resourceId: this.pageSpec + '_ContextForm',
      busFormSpec: contextFormBusSpec,
      isContextForm: true,
      isReadOnly: true
    };

    // no urls for a page context form. pass skipFilloutUrls to the formUrlInfo builder.
    this.contextFormUrlInfo = this.lmUrlInfoBuilderService.getFormUrlInfoFromFormSpec(this.contextFormSpec, { skipFilloutUrls: true });

    if (this.pageSpec.pageSpec.contextFormInfo.dataView) {
      this.contextFormDataView = this.pageSpec.pageSpec.contextFormInfo.dataView;
    }
  }

  private buildBackgroundInfo(contextFormBusSpec: LMBusFormSpec): void {
    if (!contextFormBusSpec.contextForm.background) {
      return;
    }
    const background = contextFormBusSpec.contextForm.background;
    this.useThemeColor = background.useThemeColor;
    this.useBackgroundImage = background.useBackgroundImage;

    if (this.useThemeColor) {
      // theme is only set when navigating after load, so need to subscribe to the theme and color changes.
      // only have lighter theme context forms if in the light themes
      if(this.lmThemeService.getCurrentThemeId().includes('light')) {
        this.lightContextForm = true;
        this.useHeaderColorOnTabs = (this.lmThemeService.getCurrentThemeId() === 'theme-new-light' && this.lmThemeService.getCurrentColorId() === LMColorId.alabaster);
        this.contextFormColorClass = this.lmThemeService.getContextFormBackgroundColorClass();
      }

      // scope to only the light themes for now
      const themeSubject = this.lmThemeService.getThemeChangeSubject().subscribe((theme: string) => {
        if (theme?.includes('light')) {
          this.lightContextForm = true;
          this.useHeaderColorOnTabs = (this.lmThemeService.getCurrentThemeId() === 'theme-new-light' && this.lmThemeService.getCurrentColorId() === LMColorId.alabaster);
          this.contextFormColorClass = this.lmThemeService.getContextFormBackgroundColorClass();
        } else {
          this.lightContextForm = false;
          this.useHeaderColorOnTabs = true;
          this.contextFormColorClass = '';
          this.isPersonalizable = this.canPersonalize();
        }
      });

      this.subscriptions.push(themeSubject);

      const colorSubject = this.lmThemeService.getColorChangeSubject().subscribe((color: string) => {
        if (this.lmThemeService.getCurrentThemeId().includes('light')) {
          this.contextFormColorClass = this.lmThemeService.getContextFormBackgroundColorClass(color);
          this.useHeaderColorOnTabs = (this.lmThemeService.getCurrentThemeId() === 'theme-new-light' && this.lmThemeService.getCurrentColorId() === LMColorId.alabaster);
        } else {
          this.lightContextForm = false;
          this.useHeaderColorOnTabs = true;
          this.contextFormColorClass = '';
          this.isPersonalizable = this.canPersonalize();
        }
      });

      this.subscriptions.push(colorSubject);
    }

    if (this.useBackgroundImage) {

      this.backgroundImageUrl = this._sanitizer.bypassSecurityTrustStyle(`url('${background.image}')`);
    }

    if (background?.foregroundText) {
      this.foregroundDark = background.foregroundText === 'dark';
      this.foregroundLight = background.foregroundText === 'light';
    }
  }

  private handleNewSinglePanelSelection() {
    this.isSinglePanel = this.isSinglePanelPage;
    setTimeout(() => {
      const pagePanelComponent = this.pagePanelComponentList.toArray()[0];
      this.pageState.selectedTabId = pagePanelComponent.panel.id;
      if (pagePanelComponent) {
        this.activatePanel(pagePanelComponent);
      }
    });
  }

  // when update the lmtaboptions (rebuilding the array), this tells angualar whether or not the object changed
  // keeps templates from being reinitialized for the same lmb-page-panel spec
  trackById(index: number, tab: LMTab): string {
    return tab.id;
  }

  onBeforeMoreMenuOpen = (response: SohoPopupMenuResponseFunction, options: any) => {
    let buttons;
    if (this.bannerInRoute) {
      buttons = this.toolbar.flexMoreButtons;
      if (options.isSubmenu) {
        // if a submenu, then need all the buttons for flex toolbar. This is because a menu button could get overflowed
        // and since it was in the buttonset, it wasn't in the flexMoreButtons
        buttons = this.toolbar.flexButtons;
      }
    } else if (this.isRouterOutletRootComponent) {
      buttons = this.lmBannerHeaderCompRef.instance.lmToolbarComponent.flexMoreButtons;
      if (options.isSubmenu) {
        // if a submenu, then need all the buttons for flex toolbar. This is because a menu button could get overflowed
        // and since it was in the buttonset, it wasn't in the flexMoreButtons
        buttons = this.lmBannerHeaderCompRef.instance.lmToolbarComponent.flexButtons;
      }
    } else {
      buttons = this.toolbar.flexMoreButtons;
      if (options.isSubmenu) {
        // if a submenu, then need all the buttons for flex toolbar. This is because a menu button could get overflowed
        // and since it was in the buttonset, it wasn't in the flexMoreButtons
        buttons = this.toolbar.flexButtons;
      }
    }

    this.lmPageToolbarService.buildMoreButtonMenu(this.pageSpec, response, options, buttons, null);
  };

  public toolbarMoreMenuSettings: SohoPopupMenuOptions = {
    beforeOpen: this.onBeforeMoreMenuOpen,
    attachToBody: true
  };


  private panelHasContent(panel: LMPagePanelSpec): boolean {
    if (panel.panelType === 'DashBoardPanel') {
      // needs to be an any because the types are messed up
      const dashBoardPanel = <LMMultiPagePaneSpec><unknown>panel;
      return this.dashBoardHasContent(dashBoardPanel.panes);
    } else {
      return true;
    }
  }

  private dashBoardHasContent( panes: Array<LMPagePanelSpec>): boolean {
    if (panes?.length > 0) {
      for (let i = 0, len = panes.length; i < len; i++) {
        const pane: LMPagePaneSpec = panes[i];
        let isVisible = true;

        if (this.lmWebAppService.isPagePaneInaccessible(pane)) {
          isVisible = false;
        }

        if (this.dataView && pane.visibility) {
          if (!this.lmConditionUtilService.isValid([pane.visibility], this.dataView)) {
            isVisible = false;
          }
        }

        if (isVisible) {
          return true;
        }

      }

      return false;
    }
  }
}
